import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { bulkContractService } from '../../services/bulkContractService';
import { templateService } from '../../services/templateService';

const BulkNoticeModal = ({
    isOpen,
    onClose,
    onSuccess,
    bulkEntry
}) => {
    const [loading, setLoading] = useState(false);

    // Fetch templates
    const { data: templates = [] } = useQuery(
        'templates',
        templateService.getActive,
        {
            enabled: isOpen,
            onError: (error) => {
                console.error('Failed to load templates:', error);
            }
        }
    );

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            templateId: '',
            terminationArticle: '6.2',
            autoGeneratePdf: true,
            sendToClients: false
        }
    });

    useEffect(() => {
        if (isOpen) {
            reset({
                templateId: '',
                terminationArticle: '6.2',
                autoGeneratePdf: true,
                sendToClients: false
            });
        }
    }, [isOpen, reset]);

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            const requestData = {
                bulkContractEntryId: bulkEntry.id,
                templateId: data.templateId ? parseInt(data.templateId) : null,
                terminationArticle: data.terminationArticle,
                autoGeneratePdf: data.autoGeneratePdf,
                sendToClients: data.sendToClients
            };

            await bulkContractService.createBulkNotices(bulkEntry.id, requestData);
            toast.success(`${bulkEntry.totalContracts} notificări de reziliere create cu succes!`);
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error creating bulk notices:', error);
            toast.error(error.response?.data?.message || 'Eroare la crearea notificărilor în bulk');
        } finally {
            setLoading(false);
        }
    };

    if (!bulkEntry) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Creare Notificări de Reziliere în Bulk"
            size="md"
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Warning */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start">
                        <svg className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <p className="text-sm font-medium text-yellow-800">
                                Atenție!
                            </p>
                            <p className="text-sm text-yellow-700 mt-1">
                                Se vor crea <span className="font-semibold">{bulkEntry.totalContracts} notificări de reziliere</span> pentru toate contractele din acest bulk entry.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Template Selection */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Template Notificare Reziliere
                    </label>
                    <select
                        {...register('templateId')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Template implicit (Reziliere)</option>
                        {templates
                            .filter(t => t.name?.toLowerCase().includes('reziliere') || t.name?.toLowerCase().includes('notificare'))
                            .map((template) => (
                                <option key={template.id} value={template.id}>
                                    {template.name}
                                </option>
                            ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                        Dacă nu selectezi un template, se va folosi template-ul implicit "Reziliere"
                    </p>
                </div>

                {/* Termination Article */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Articol de Reziliere
                    </label>
                    <input
                        type="text"
                        {...register('terminationArticle', {
                            maxLength: { value: 100, message: 'Articolul nu poate depăși 100 de caractere' }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="ex: 6.2"
                    />
                    {errors.terminationArticle && (
                        <p className="text-red-500 text-xs mt-1">{errors.terminationArticle.message}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                        Articolul din contract pe baza căruia se emite notificarea de reziliere
                    </p>
                </div>

                {/* Options */}
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            {...register('autoGeneratePdf')}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                            Generează automat PDF-urile
                        </span>
                    </label>

                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            {...register('sendToClients')}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                            Trimite automat notificările către clienți
                        </span>
                    </label>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Anulează
                    </Button>
                    <Button
                        type="submit"
                        loading={loading}
                        disabled={loading}
                        variant="danger"
                    >
                        Creează {bulkEntry.totalContracts} Notificări
                    </Button>
                </div>
            </form>
        </Modal>
    );
};
export default BulkNoticeModal;